import type { IVector3 } from '@seddi/math-v2';
import {
  Color,
  HalfFloatType,
  MathUtils,
  Object3D,
  PerspectiveCamera,
  Scene,
  Timer,
  Vector4,
  type WebGLRenderer,
  WebGLRenderTarget,
} from '@seddi/three';
import { EffectComposer } from '@seddi/three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from '@seddi/three/addons/postprocessing/OutputPass.js';
import type { RenderPass } from '@seddi/three/addons/postprocessing/RenderPass.js';
import { Pointer } from '@seddi/ui-viewport-core';
import log from 'loglevel';
import { type CSSProperties, type RefObject, Component, createRef } from 'react';
import styled from 'styled-components';

import { assert } from '../../core/assert';
import { Viewport } from '../../core/viewport';
import { SceneGeometryModule } from '../../domain/scene/sceneGeometryModule';
import type { SceneDesc } from '../../domain/scene/sceneTypes';
import * as FlagsModule from '../../flags/flagsModule';
import { type FeatureFlagsState, FeatureFlag } from '../../flags/flagsTypes';
import {
  type SceneRenderable,
  create as createSceneRenderable,
} from '../../renderables/sceneRenderable';
import { ThreeMainRenderPass } from '../../renderer3/passes/threeMainRenderPass';
import { OutlineSelectionPass } from '../../renderer3/passes/outlineSelectionPass';
import { DefaultRenderingPipeline } from '../../renderer3/defaultRenderingPipeline';
import type { ResourceEvent } from '../../resources/resourceEvent';
import { ResourcesContext } from '../../resources/resourcesContext';
import { SceneReconciler } from '../../sceneReconciler/sceneReconciler';
import type { VisibilityState } from '../../sceneReconciler/visibilityState';
import type { Theme } from '../../theme/themeTypes';
import { create as createObject } from '../../three/core/object3d';
import { MsaaRenderPass } from '../../three/postprocessing/msaaRenderPass';
import {
  createRenderer,
  createRendererContextAttributes,
  readRenderTargetPixelsFloat16,
  setRendererSize,
} from '../../three/renderers/webGlRenderer';
import { convertToneMapping } from '../../three/toneMapping';
import { type SceneViewerConfig, create as createConfig } from '../../sceneViewerConfig';
import { GizmoModes } from './tools/gizmoTypes';
import type { ITool } from './tools/toolTypes';
import { AuthorCamera } from './authorCamera';

type AuthorCameraProps = { distance: number; origin: IVector3; pitch: number; yaw: number };

type AuthorCameraView = {
  position: IVector3;
  root: { position: IVector3; rotation: IVector3 };
  rotation: IVector3;
};

const cameraPropsDefault = {
  distance: 2.2,
  origin: { x: 0, y: 1, z: 0 },
  pitch: 0,
  yaw: 0,
};

const cameraViewDefault = {
  position: { x: 0, y: 0, z: 1.6987071125357485 },
  rotation: { x: 0, y: 0, z: 0 },
  root: {
    position: { x: 0, y: 1, z: 0 },
    rotation: { x: -0.21915926535897923, y: 0.01, z: 0 },
  },
};

const EventTypes = ['resourcechange', 'resourcesprogress'];

const StyledCanvas = styled.canvas`
  height: 100%;
  width: 100%;
`;

type Cursor = {
  image: string;
  isVisible: boolean;
  position: string;
  rotation: number;
  shouldUpdate: boolean;
};

export type ViewerProps = {
  actions?: { showAnimatedCursor?: (cursor: any) => void; hover?: (ids: string[]) => void };
  backgroundAlpha?: number;
  backgroundColor?: CSSProperties['color'];
  cameraControls?: 'freelook' | 'orbit' | 'pan' | 'zoomin' | 'zoomout';
  cameraDefault?: {
    props?: AuthorCameraProps;
    view?: AuthorCameraView;
    far?: number;
    fov?: number;
    near?: number;
  };
  config?: Partial<SceneViewerConfig>;
  cursor?: Cursor;
  featureFlags?: Partial<FeatureFlagsState>;
  gizmoMode?: GizmoModes;
  hover?: string[];
  id?: string;
  isRender?: boolean;
  /**
   * @deprecated Used only by Author to track scene loading progress.
   */
  onDrawEvent?: Function;
  onUpdate?: Function;
  postprocessing?: string[];
  preserveDrawingBuffer?: boolean;
  scene: SceneDesc;
  selection?: string[];
  showGarmentGeometry?: boolean;
  theme?: Theme;
  useBrandLogos?: boolean;
  visible?: VisibilityState;
};

export type ViewerState = {};

export class Viewer<P extends ViewerProps, S extends ViewerState> extends Component<P, S> {
  readonly canvasRef: RefObject<HTMLCanvasElement>;

  readonly config: SceneViewerConfig;

  // TODO: is this property used anywhere or just exposed to the public interface?
  readonly viewport: Viewport;

  renderer: WebGLRenderer;
  scene: Scene;
  renderGroup: Object3D;
  camera: AuthorCamera;

  cursor: Cursor;
  pointer: Pointer;
  tool: ITool;

  needsUpdate: boolean;
  onDrawEventsStack: Function[] = [];
  outlined: Object3D[];

  resources: ResourcesContext;

  protected isMounted: boolean;
  protected sceneReconciler: SceneReconciler;
  protected sceneRenderable: SceneRenderable;

  protected handleResourceChange: EventListener;

  private backgroundAlpha: number;
  private readonly backgroundColor: Color;

  private readonly timer: Timer;
  private resizeObserver: ResizeObserver | null;

  private composer: EffectComposer;
  private renderPass: RenderPass;
  private outlinePass: OutlineSelectionPass;
  private outputPass: OutputPass;

  constructor(props: P) {
    super(props);

    this.canvasRef = createRef<HTMLCanvasElement>();

    this.backgroundAlpha = 1;
    this.backgroundColor = new Color(0xffffff);

    // TODO: config and featureFlags seem to be two flavors for the same coin
    this.config = createConfig(props.config);
    // TODO: instance initialization affects globally the flags state which can be confusing
    FlagsModule.init(props.featureFlags);

    this.viewport = new Viewport(0, 0, 500, 500);

    this.isMounted = false;
    this.outlined = [];

    this.setCursor = this.setCursor.bind(this);

    // init timer
    this.timer = new Timer();
    // init resize observer
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { contentBoxSize, devicePixelContentBoxSize } = entry;
        // Limited availability
        // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/devicePixelContentBoxSize
        if (devicePixelContentBoxSize) {
          const size = devicePixelContentBoxSize[0];
          log.debug('device pixel content box size', size.inlineSize, size.blockSize);
        }

        const size = contentBoxSize[0];
        this.setSize(size.inlineSize, size.blockSize);
      }
    });

    this.handleResourceChange = ((event: ResourceEvent) => {
      if (event.kind === 'Environment') {
        this.updateBackground();
      }

      // TODO: remove
      // used by Author to track scene loading progress
      if (this.props.onDrawEvent) {
        const onDrawEvent = this.props.onDrawEvent;
        this.onDrawEventsStack.push(() => onDrawEvent({ id: event.id }));
      }
    }) as EventListener;
  }

  componentDidMount() {
    const canvas = this.canvasRef.current!;
    const {
      props: {
        actions,
        backgroundAlpha,
        backgroundColor,
        scene,
        showGarmentGeometry,
        theme,
        useBrandLogos,
        visible,
      },
    } = this;

    // configure background alpha
    if (backgroundAlpha !== undefined) {
      this.backgroundAlpha = Math.max(Math.min(backgroundAlpha, 1), 0);
    }

    // configure background color
    if (backgroundColor) {
      this.backgroundColor.set(backgroundColor);
    } else {
      // configure background color from parent
      const parent = canvas.parentNode as Element;
      const parentBackgroundColor = getComputedStyle(parent).getPropertyValue('background-color');
      this.backgroundColor.set(parentBackgroundColor);
    }

    // listen to canvas resize events
    this.resizeObserver!.observe(canvas, { box: 'content-box' });

    this.initRender();

    // Init camera.
    const { cameraDefault } = this.props;
    const { view, props } = cameraDefault;

    const cameraArgs = {
      far: cameraDefault.far,
      fov: cameraDefault.fov,
      near: cameraDefault.near,
      ...(props ?? cameraPropsDefault),
    };

    this.camera = new AuthorCamera(cameraArgs);

    if (view) {
      this.updateCamera(view);
    }

    // Attach camera.
    this.renderPass.camera = this.camera;
    this.outlinePass.camera = this.camera;

    // Init pointer.
    this.pointer = new Pointer({ canvas });

    // Init framebuffers after camera and pointer initialization.
    this.setSize(canvas.clientWidth, canvas.clientHeight);

    // Create default scene objects.
    this.renderGroup = createObject('Default', this.scene);
    this.renderGroup.add(this.camera.root);

    // Init scene renderable.
    const sceneRenderable = createSceneRenderable(this.scene);
    this.sceneRenderable = sceneRenderable;

    // Init scene reconciler.
    const sceneReconciler = new SceneReconciler(
      this.resources,
      this.sceneRenderable,
      this.config,
      theme,
    );
    this.sceneReconciler = sceneReconciler;

    // listen to resource management events
    // NOTE: registration order matters, keep this one after scene reconciler constructor
    this.resources.addEventListener('resourcechange', this.handleResourceChange);

    sceneReconciler.update(scene, showGarmentGeometry);
    sceneReconciler.updateBrandLogosVisibility(useBrandLogos ?? false);
    sceneReconciler.updateVisible(visible, this.outlined);
    this.resources.commit();

    // reset cursor on sceneLoad
    actions.showAnimatedCursor?.({});

    this.isMounted = true;

    this.renderer.setAnimationLoop(this.onAnimationTick.bind(this));
  }

  shouldComponentUpdate(nextProps: Readonly<P>) {
    const { props } = this;

    let isUpdate = false;

    if (this.isSceneChanged(nextProps, props)) {
      isUpdate = this.updateScene(nextProps, props) || isUpdate;
      // Force update when resource request is pending
      isUpdate ||= this.resources.hasLoadPending();
    }

    if (this.isBackgroundChanged(nextProps, props)) {
      this.updateBackground(nextProps);
      isUpdate = true;
    }

    this.needsUpdate ||= isUpdate;

    return isUpdate;
  }

  componentDidUpdate(_prevProps: P, _prevState: S) {
    this.resources.commit();
  }

  componentWillUnmount() {
    // clean up subscriptions
    this.resizeObserver!.disconnect();
    this.resizeObserver = null;

    this.tool && this.tool.dispose();
    this.tool = undefined;

    this.pointer && this.pointer.dispose();
    this.pointer = undefined;

    this.resources &&
      this.resources.removeEventListener('resourcechange', this.handleResourceChange);
    this.resources && this.resources.dispose();
    this.resources = undefined;

    // TODO: this should go with scene reconciler dispose
    const geometryCache = SceneGeometryModule.instance.geometryCache;
    geometryCache.dispose();

    this.renderer?.setAnimationLoop(null);
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
  }

  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    assert(EventTypes.includes(type), 'Unrecognized event type');
    switch (type) {
      case 'resourcechange':
      case 'resourcesprogress': {
        this.resources.addEventListener(type, callback, options);
        break;
      }
    }
  }

  removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void {
    assert(EventTypes.includes(type), 'Unrecognized event type');
    switch (type) {
      case 'resourcechange':
      case 'resourcesprogress': {
        this.resources.removeEventListener(type, callback, options);
        break;
      }
    }
  }

  onAnimationTick() {
    const { pointer, resources, sceneReconciler, setCursor, timer, tool } = this;

    const delta = timer.getDelta();
    const elapsed = timer.getElapsed();

    const toolneedsUpdate = tool.onAnimationTick({
      delta,
      pointer: { ...pointer.getFrame(), setCursor },
      time: elapsed,
    });

    // Draw from resources.
    if (resources.needsUpdate) {
      this.needsUpdate = true;
      resources.needsUpdate = false;
    }

    if (sceneReconciler.needsUpdate) {
      this.needsUpdate = true;
      sceneReconciler.needsUpdate = false;
    }

    // Draw from tool.
    if (toolneedsUpdate) {
      this.needsUpdate = true;
    }

    if (!this.needsUpdate) {
      return;
    }

    this.draw();

    const { onUpdate } = this.props;
    if (onUpdate) {
      onUpdate();
    }

    this.onDraw();
  }

  setSize(width: number, height: number) {
    const devicePixelRatio = window.devicePixelRatio;
    // update renderer
    setRendererSize(this.renderer, width, height, devicePixelRatio);
    // update camera projection matrix
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    // update renderer composition manager
    const displayWidth = Math.round(width * devicePixelRatio);
    const displayHeight = Math.round(height * devicePixelRatio);
    log.debug('set size', displayWidth, displayHeight);
    this.composer.setSize(displayWidth, displayHeight);
    // update other stuff
    this.pointer.setSize(width, height);
    this.viewport.set(0, 0, displayWidth, displayHeight);
    this.needsUpdate = true;
  }

  async downloadFrame(minSize = 2048) {
    // Get canvas aspect ratio
    const aspectRatio = this.viewport.width / this.viewport.height;

    // Calculate a target size fort exportFrame with the smallest canvas side being 'minSize'
    let targetWidth = 0;
    let targetHeight = 0;
    if (this.viewport.width < this.viewport.height) {
      targetWidth = minSize;
      targetHeight = Math.round(minSize / aspectRatio);
    } else {
      targetWidth = Math.round(minSize * aspectRatio);
      targetHeight = minSize;
    }

    const blobURL = await this.exportFrame(targetWidth, targetHeight);
    const a = document.createElement('a');
    a.href = blobURL;
    if (!(window as any).chrome) {
      document.body.appendChild(a);
    }
    a.download = `webgl-${Date.now()}.png`;
    a.click();
    a.remove();
    URL.revokeObjectURL(blobURL);
  }

  draw() {
    if (!this.isMounted) return;

    this.renderer.clear();
    this.composer.render();

    this.needsUpdate = false;
  }

  onDraw() {
    this.onDrawEventsStack.forEach((callback) => callback());
    this.onDrawEventsStack = [];
  }

  getCamera() {
    const { camera } = this;
    const translation = camera.getWorldPosition(camera.position.clone());
    const rotation = camera.getWorldQuaternion(camera.quaternion.clone());
    return {
      far: camera.far,
      fov: MathUtils.degToRad(camera.fov),
      near: camera.near,
      transform: {
        rotation: { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w },
        translation: { x: translation.x, y: translation.y, z: translation.z },
      },
      type: 'perspective',
    };
  }

  /**
   * @deprecated Author specific behavior, move it out.
   */
  getDecomposedCamera() {
    const { camera } = this;
    const { position, rotation, root } = camera;
    const { position: rootPosition, rotation: rootRotation } = root;
    return {
      position: { x: position.x, y: position.y, z: position.z },
      rotation: { x: rotation.x, y: rotation.y, z: rotation.z },
      root: {
        position: { x: rootPosition.x, y: rootPosition.y, z: rootPosition.z },
        rotation: { x: rootRotation.x, y: rootRotation.y, z: rootRotation.z },
      },
    };
  }

  /**
   * @deprecated Author specific behavior, move it out.
   */
  updateCamera({ root, position, rotation }) {
    const { camera } = this;
    camera.root.position.copy(root.position);
    camera.root.rotation.set(root.rotation.x, root.rotation.y, root.rotation.z);
    camera.position.copy(position);
    camera.rotation.set(rotation.x, rotation.y, rotation.z);
    camera.root.updateMatrixWorld();
    this.needsUpdate = true;
  }

  resetCamera() {
    const { camera } = this;
    camera.reset();
    this.needsUpdate = true;
  }

  setCursor(cursor) {
    const { actions, cursor: prevCursor } = this.props;
    if (
      cursor.image &&
      (cursor.image !== prevCursor?.image ||
        (cursor.rotation !== prevCursor?.rotation && (cursor.rotation || prevCursor?.rotation))) &&
      this.pointer.hover
    ) {
      actions.showAnimatedCursor?.(cursor);
      this.cursor = cursor;
    }
  }

  handleMouseLeave() {
    this.props.actions.hover?.([]);
  }

  render() {
    // TODO: draw should trigger with a mechanism independent from React
    if (this.needsUpdate) {
      this.draw();
    }

    const { id } = this.props;
    return (
      <StyledCanvas id={id} ref={this.canvasRef} onMouseLeave={() => this.handleMouseLeave()} />
    );
  }

  async exportFrame(width = 2048, height = 2048, quality = 0): Promise<string> {
    const { renderer, scene } = this;

    // Save renderer state
    const prevViewport = renderer.getViewport(new Vector4());
    const prevScissor = renderer.getScissor(new Vector4());
    const prevScissorTest = renderer.getScissorTest();

    // Create camera and render target with dimensions
    const camera = this.createExportCamera(width, height);
    const renderTarget = new WebGLRenderTarget(width, height, { type: HalfFloatType });
    // Create render pipeline with dimensions
    const pipeline = new DefaultRenderingPipeline(renderer, scene, camera, renderTarget);
    // Do the render
    pipeline.composer.renderToScreen = false;
    pipeline.render();
    // Read from render target and decode
    const data = await readRenderTargetPixelsFloat16(renderer, pipeline.composer.readBuffer);

    // Cleanup and restore
    pipeline.dispose();
    renderer.setViewport(prevViewport);
    renderer.setScissor(prevScissor);
    renderer.setScissorTest(prevScissorTest);

    const image = encodeImageData(width, height, data, quality);

    return image;
  }

  protected initRender() {
    const {
      config,
      props: { preserveDrawingBuffer },
    } = this;

    // Initialize renderer.
    {
      const { backgroundAlpha, backgroundColor } = this;
      const contextAttributes = createRendererContextAttributes();
      contextAttributes.preserveDrawingBuffer = preserveDrawingBuffer;
      contextAttributes.alpha = backgroundAlpha !== 1;
      const canvas = this.canvasRef.current!;
      const renderer = createRenderer(canvas, contextAttributes);
      renderer.toneMapping = convertToneMapping(config.toneMapping);
      renderer.toneMappingExposure = config.toneMappingExposure;
      renderer.setClearColor(backgroundColor, backgroundAlpha);
      this.renderer = renderer;
    }

    const { renderer } = this;

    // Initialize resources context.
    {
      const resourcesOptions = { ...config };
      const resources = new ResourcesContext(renderer, resourcesOptions);
      this.resources = resources;
    }

    // Initialize scene.
    const isDepthPeeling = FlagsModule.isEnabled(FeatureFlag.DEPTH_PEELING);
    const scene = new Scene();
    {
      if (isDepthPeeling) {
        const depthPeelingLayers = FlagsModule.getValue(FeatureFlag.DEPTH_PEELING) as number;
        (scene as any).depthPeelingLayers = depthPeelingLayers;
        (scene as any).depthPeelingBias = 0;
      }
      this.scene = scene;
    }

    // Initialize renderer compositor.
    {
      const composer = new EffectComposer(renderer);
      this.renderPass = isDepthPeeling
        ? new ThreeMainRenderPass(this.scene, this.camera)
        : new MsaaRenderPass(this.scene, this.camera);
      this.outlinePass = new OutlineSelectionPass(renderer, this.scene, this.camera);
      this.outputPass = new OutputPass();
      composer.addPass(this.renderPass);
      composer.addPass(this.outlinePass);
      composer.addPass(this.outputPass);
      this.composer = composer;
    }

    this.updateBackground();
  }

  protected isBackgroundChanged(nextProps: Readonly<P>, props: Readonly<P>) {
    if (nextProps.isRender !== props.isRender) {
      return true;
    }
    if (nextProps.visible.background !== props.visible.background) {
      return true;
    }
    if (nextProps.isRender && nextProps.scene?.children?.length) {
      const nextEntity = nextProps.scene.children.find((e) => e.components.environment);
      const prevEntity = props.scene.children.find((e) => e.components.environment);
      if (
        nextEntity?.components.environment.environment?.id !==
        prevEntity?.components.environment.environment?.id
      ) {
        return true;
      }
    }
    return false;
  }

  protected isSceneChanged(nextProps: Readonly<P>, props: Readonly<P>) {
    return (
      nextProps.scene !== props.scene ||
      nextProps.hover !== props.hover ||
      nextProps.selection !== props.selection ||
      nextProps.visible !== props.visible ||
      nextProps.showGarmentGeometry !== props.showGarmentGeometry ||
      nextProps.useBrandLogos !== props.useBrandLogos ||
      nextProps.gizmoMode !== props.gizmoMode
    );
  }

  protected updateScene(nextProps: Readonly<P>, prevProps: Readonly<P>) {
    if (!this.isMounted) {
      return false;
    }

    const { outlined, sceneReconciler } = this;

    let dirty = false;
    let sceneDirty = false;

    // Update scene
    if (
      nextProps.scene !== prevProps.scene ||
      nextProps.showGarmentGeometry !== prevProps.showGarmentGeometry
    ) {
      sceneDirty = sceneReconciler.update(nextProps.scene, nextProps.showGarmentGeometry);
      dirty = dirty || sceneDirty;
    }

    // Update outlined renderables
    if (
      prevProps.hover !== nextProps.hover ||
      prevProps.selection !== nextProps.selection ||
      dirty
    ) {
      const { renderables } = sceneReconciler;
      outlined.length = 0;

      for (const id of nextProps.hover) {
        if (renderables.has(id)) {
          outlined.push(renderables.get(id));
        }
      }
      for (const id of nextProps.selection) {
        if (renderables.has(id)) {
          outlined.push(renderables.get(id));
        }
      }

      dirty = true;
    }

    // Update visible elements.
    if (prevProps.visible !== nextProps.visible || sceneDirty) {
      sceneReconciler.updateVisible(nextProps.visible, outlined, nextProps.isRender);
      dirty = true;
    }

    if (prevProps.useBrandLogos !== nextProps.useBrandLogos || sceneDirty) {
      sceneReconciler.updateBrandLogosVisibility(nextProps.useBrandLogos ?? false);
      dirty = true;
    }

    this.outlinePass.objects = outlined;

    return dirty;
  }

  protected updateBackground(nextProps?: Readonly<P>) {
    const { backgroundAlpha, backgroundColor, props, scene } = this;

    if (backgroundAlpha !== 1.0) {
      return;
    }

    nextProps = nextProps ?? props;

    if (nextProps.isRender) {
      // Render mode selectively shows background.
      scene.background = nextProps.visible.background ? scene.environment : backgroundColor;
    } else {
      // Default viewport never shows background.
      scene.background = backgroundColor;
    }

    this.needsUpdate = true;
  }

  private createExportCamera(width: number, height: number): PerspectiveCamera {
    // Clone the live camera
    const exportCamera = this.camera.clone() as PerspectiveCamera;

    // Update aspect ratio to match export resolution
    exportCamera.aspect = width / height;
    exportCamera.updateProjectionMatrix();

    return exportCamera;
  }

  static defaultProps: Partial<ViewerProps> = {
    actions: { hover: () => {}, showAnimatedCursor: () => {} },
    cameraControls: 'orbit',
    cameraDefault: {
      props: cameraPropsDefault,
      view: cameraViewDefault,
      far: 50,
      fov: 20,
      near: 0.01,
    },
    cursor: {
      image:
        "data:image/svg+xml,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cg clip-path='url(%23clip0_13281_1530)'%3e%3cmask id='path-1-outside-1_13281_1530' maskUnits='userSpaceOnUse' x='-1' y='0' width='18' height='16' fill='black'%3e%3crect fill='white' x='-1' width='18' height='16'/%3e%3cpath d='M1.68435 1.05519L14.7529 7.90637C15.1189 8.09826 15.0673 8.63146 14.671 8.7513L9.41576 10.3409C9.30803 10.3735 9.21593 10.4436 9.15659 10.5382L6.49458 14.7825C6.28866 15.1108 5.78937 15.0568 5.66025 14.6923L1.02733 1.6126C0.887599 1.2181 1.31058 0.859243 1.68435 1.05519Z'/%3e%3c/mask%3e%3cpath d='M1.68435 1.05519L14.7529 7.90637C15.1189 8.09826 15.0673 8.63146 14.671 8.7513L9.41576 10.3409C9.30803 10.3735 9.21593 10.4436 9.15659 10.5382L6.49458 14.7825C6.28866 15.1108 5.78937 15.0568 5.66025 14.6923L1.02733 1.6126C0.887599 1.2181 1.31058 0.859243 1.68435 1.05519Z' fill='%230B0B0C'/%3e%3cpath d='M1.68435 1.05519L1.22003 1.94086L1.22003 1.94086L1.68435 1.05519ZM14.7529 7.90637L15.2172 7.0207L15.2172 7.0207L14.7529 7.90637ZM14.671 8.7513L14.9606 9.70847L14.671 8.7513ZM9.41576 10.3409L9.12625 9.3837L9.12624 9.3837L9.41576 10.3409ZM9.15659 10.5382L10.0038 11.0695L10.0038 11.0695L9.15659 10.5382ZM6.49458 14.7825L5.64742 14.2511V14.2511L6.49458 14.7825ZM5.66025 14.6923L4.71763 15.0262L4.71764 15.0262L5.66025 14.6923ZM1.02733 1.6126L0.0847181 1.94648L0.0847183 1.94648L1.02733 1.6126ZM1.22003 1.94086L14.2886 8.79204L15.2172 7.0207L2.14866 0.169519L1.22003 1.94086ZM14.2886 8.79204C13.8555 8.565 13.9277 7.9314 14.3815 7.79413L14.9606 9.70847C16.2068 9.33152 16.3823 7.63151 15.2172 7.0207L14.2886 8.79204ZM14.3815 7.79413L9.12625 9.3837L9.70528 11.298L14.9606 9.70847L14.3815 7.79413ZM9.12624 9.3837C8.78932 9.48561 8.49823 9.70582 8.30943 10.0068L10.0038 11.0695C9.93362 11.1813 9.82673 11.2613 9.70529 11.298L9.12624 9.3837ZM8.30943 10.0068L5.64742 14.2511L7.34174 15.3138L10.0038 11.0695L8.30943 10.0068ZM5.64742 14.2511C5.88237 13.8765 6.45051 13.9283 6.60286 14.3584L4.71764 15.0262C5.12824 16.1854 6.69495 16.3451 7.34174 15.3138L5.64742 14.2511ZM6.60287 14.3584L1.96995 1.27872L0.0847183 1.94648L4.71763 15.0262L6.60287 14.3584ZM1.96995 1.27872C2.14672 1.77778 1.6172 2.14908 1.22003 1.94086L2.14866 0.169519C1.00396 -0.430591 -0.371521 0.658423 0.0847181 1.94648L1.96995 1.27872Z' fill='white' mask='url(%23path-1-outside-1_13281_1530)'/%3e%3c/g%3e%3cdefs%3e%3cclipPath id='clip0_13281_1530'%3e%3crect width='24' height='24' fill='white'/%3e%3c/clipPath%3e%3c/defs%3e%3c/svg%3e",
      isVisible: true,
      position: 'upperLeft',
      rotation: 0,
      shouldUpdate: false,
    },
    gizmoMode: GizmoModes.NONE,
    hover: [],
    id: 'Viewer',
    isRender: false,
    postprocessing: [],
    preserveDrawingBuffer: false,
    selection: [],
    theme: { threads: [], buttonElements: [] },
    showGarmentGeometry: false,
    useBrandLogos: false,
    visible: {
      avatar: true,
      background: true,
      garmentPiece: true,
      garmentSeam: true,
      garmentHem: true,
      ground: true,
      logos: true,
    },
  };
}

const encodeImageData = (
  width: number,
  height: number,
  data: Uint8ClampedArray,
  quality?: number,
): string => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.createImageData(width, height);
  // Flip Y
  const lineStride = width * 4;
  for (let y = 0; y < height; y++) {
    const srcBegin = (height - y - 1) * lineStride;
    const dstBegin = y * lineStride;
    imageData.data.set(data.subarray(srcBegin, srcBegin + lineStride), dstBegin);
  }
  ctx.putImageData(imageData, 0, 0);

  if (quality) {
    return canvas.toDataURL('image/png', quality);
  }
  return canvas.toDataURL('image/png');
};
